import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FaTimes } from 'react-icons/fa';
import closeIcon from '../../images/common/close.svg';
import StyledTappable from './StyledTappable';
import axios from 'axios';
import { Document, Page, pdfjs } from 'react-pdf';

import downloadIcon from '../../images/consultations/download-dark.svg';
import { useDispatch, useSelector } from 'react-redux';
import { closeFileViewer } from '../../actions';
import LoadingComponent from './LoadingComponent';
import Modal from '../../containers/common/Modal';
import { FlyInTransition } from '../transitions/transitions';

const OuterContainer = styled.div`
  background: #fff;
  border-radius: 12px 12px 0px 0px;
  height: 95%;
  width: 100vw;
  position: absolute;
  z-index: 100;
  bottom: 0;
  padding: 12px 6px;
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 800px;
  @media only screen and (max-width: 440px) {
    height: 100%;
    max-width: 440px;
  }
`;
const Header = styled.div`
  font-family: 14px;
  font-weight: 500;
  width: 100%;
  padding: 10px;
  padding-top: 0px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #f0f3f6;
`;
const CloseContainer = styled(StyledTappable)`
  width: 1.625rem;
  height: 1.625rem;
  border-radius: 4px;
  border: 1px solid #eee;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Btn = styled.div`
  padding: 8px;
  border-radius: 4px;
  border: 1px solid #f0f0f0;
  width: fit-content;
  font-size: 14px;
  height: 24px;
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: 16px;
  text-align: center;
  cursor: pointer;
  transition: all 0.1s ease;
  &:hover {
    opacity: 0.6;
  }
  &:active {
    opacity: 0.3;
  }
`;
const FileContainer = styled.div`
  width: 100%;
  height: 100%;
  max-width: 800px;
  overflow: auto; /* Make the container scrollable */
  @media only screen and (max-width: 440px) {
    max-width: 420px;
  }
`;
const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
  align-items: center;
  padding: 4px;
  background: #ddd;
  min-height: 500px;
  width: 100%;
  max-width: 800px;
  padding-bottom: 80px;
  @media only screen and (max-width: 440px) {
    max-width: 420px;
  }
`;
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const FileRender = ({ pdfUrl, pdfData, numPages, setNumPages }) => {
  const screenWidth = window.innerWidth;
  const [pageNumber, setPageNumber] = useState(1);
  function onDocumentLoadSuccess({ numPages }) {
    setNumPages(numPages);
    setPageNumber(1);
  }
  useEffect(() => {
    return () => {
      URL.revokeObjectURL(pdfUrl);
    };
  }, [pdfUrl]);

  return (
    <div className="h-full">
      <FileContainer>
        <Document
          file={pdfData}
          renderTextLayer={false} // Disable text layer
          renderAnnotationLayer={false} // Disable annotation layer
          width={screenWidth > 800 ? '800' : screenWidth}
          onLoadSuccess={onDocumentLoadSuccess}
        >
          <PageContainer>
            {[...Array(numPages)].map((_, index) => (
              <Page
                zoom={1}
                width={screenWidth > 800 ? '780' : screenWidth - 20}
                key={`page_${index + 1}`}
                pageNumber={index + 1}
                style={{ width: '100vw' }}
              />
            ))}
          </PageContainer>
          <p>
            Page {pageNumber} of {numPages}
          </p>
        </Document>
      </FileContainer>
    </div>
  );
};

const FileViewerModal = ({ url, close }) => {
  const appFileViewer = useSelector((state) => state?.app?.appFileViewer);
  const screenWidth = window.innerWidth;
  let isOpen = null,
    title = 'PDF Viewer',
    contentUrl = null,
    type = 'pdf';

  if (appFileViewer) {
    isOpen = appFileViewer.isOpen;
    title = appFileViewer.title;
    contentUrl = appFileViewer.contentUrl;
    type = appFileViewer.type;
  }
  const dispatch = useDispatch();
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState(null);
  const [errorText, setErrorText] = useState('');
  const authToken = useSelector((state) => state.user.authToken);
  const [data, setData] = useState({ buffer: null, type: null });
  const [numPages, setNumPages] = useState(null);

  const downloadFile = () => {
    let link;
    if (type === 'pdf')
      link = document.getElementById('prescription-download1');
    else link = document.getElementById('prescription-download2');
    link.click();
  };

  const fetchPDF = async () => {
    setError(false);
    setFetching(true);
    setErrorText('');
    try {
      const response = await axios.get(contentUrl, {
        headers: {
          Authorization: authToken,
          'Content-Type': 'application/json',
        },
        responseType: 'arraybuffer',
      });
      const mimeType = response.headers['content-type'];
      const uint8Array = new Uint8Array(response.data);
      const pdfBlob = new Blob([uint8Array], { type: mimeType });
      setData({
        buffer: pdfBlob,
        type: mimeType,
      });
      setError(false);
      setErrorText('');
    } catch (error) {
      console.error('Error fetching PDF:', error);
      setError(true);
      setErrorText(error.message);
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    if (type === 'pdf') fetchPDF();
    else {
      setFetching(false);
      setError(false);
    }
  }, []);

  if (!isOpen) return null;

  function handleClose() {
    setData({ buffer: null, type: null });
    dispatch(closeFileViewer());
  }
  return (
    <Modal>
      <div className="modalContainer">
        <FlyInTransition in={isOpen} mountOnEnter unmountOnExit appear>
          <OuterContainer>
            <Header>
              <div className="flex flex-row items-center gap-2 text-body-l text-txtlight-new font-semibold">
                <p className=" ">{title}</p>
                {!fetching && !error && type === 'pdf' && (
                  <Btn onClick={downloadFile}>
                    <img
                      src={downloadIcon}
                      alt="download-icon"
                      style={{ height: '16px' }}
                    />
                    <span>Download</span>
                  </Btn>
                )}
                {numPages ? <p>Pages: {numPages}</p> : <></>}
              </div>

              <CloseContainer onTap={handleClose}>
                <img src={closeIcon} alt="close" />
              </CloseContainer>
            </Header>
            {fetching && !error && <LoadingComponent />}
            {error && (
              <div className="text-body-l mt-4">
                Some Error occurred in fetching prescription:
                <p className="text-body-l">{errorText}</p>
              </div>
            )}
            {type === 'pdf' && data?.buffer && (
              <a
                id="prescription-download1"
                download={'visit-prescription.pdf'}
                href={URL.createObjectURL(data?.buffer)}
                className="hidden"
              />
            )}
            {type !== 'pdf' && (
              <a
                id="prescription-download2"
                download={'visit-prescription'}
                href={data?.buffer}
                className="hidden"
              />
            )}
            {/* "application/octet-stream"
             */}
            {!fetching && type === 'pdf' ? (
              <FileRender
                pdfData={data.buffer}
                pdfUrl={url}
                setNumPages={setNumPages}
                numPages={numPages}
              />
            ) : (
              !fetching && (
                <img
                  src={contentUrl}
                  width={screenWidth > 800 ? '750' : screenWidth - 20}
                  alt="content"
                />
              )
            )}
          </OuterContainer>
        </FlyInTransition>
      </div>
    </Modal>
  );
};

export default FileViewerModal;
